import { BlockedScreen } from '@/components/blocked-screen';
import { ProcessingScreen } from '@/components/processing-screen';
import { useAuth } from '@/backend/context/auth-management';
import { ROUTES } from '@/lib/routes';
import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { AnalyticsService } from '@/shared/analytics/analytics-service';
import { PostHogPageView } from '@/components/providers/posthog-pageview';

export const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    // 1. Resetear el scroll global
    window.scrollTo(0, 0);
    document.documentElement.scrollTo(0, 0);
    document.body.scrollTo(0, 0);

    // 2. Resetear contenedores específicos de scroll
    // Buscamos elementos que usualmente usamos en layouts de dashboards
    const scrollableContainers = document.querySelectorAll(
      '.overflow-y-auto, main, #root > div',
    );
    scrollableContainers.forEach((container) => {
      container.scrollTo(0, 0);
    });
  }, [pathname]);

  return null;
};

export default function AuthLayout() {
  const profile = useAuth((s) => s.profile);
  const user = useAuth((s) => s.user);
  const status = useAuth((s) => s.status);
  const hasPendingPayment = useAuth((s) => s.hasPendingPayment);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // If it's still idle or loading, do nothing yet
    if (status === 'loading' || status === 'idle') {
      return;
    }

    const isLoginPage = location.pathname === ROUTES.PAGE.LOGIN;

    // 1. If we know the user is NOT logged in -> restrict them to the login page
    if (!user || status === 'unauthenticated') {
      if (!isLoginPage) {
        navigate(ROUTES.PAGE.LOGIN, { replace: true });
      }
      return;
    }

    // 3. If logged in, has profile, but trying to view the login page -> send to dashboard
    if (user && profile) {
      AnalyticsService.identify(profile.id, {
        email: profile.email || user.email,
        name: profile.name,
      });

      // Set global context for TuNomina
      AnalyticsService.setGlobalContext({
        businessId: profile.id,
        account_type: 'business',
        $host: window.location.host,
      });
      
      if (isLoginPage) {
        navigate(ROUTES.DASHBOARD.PRINCIPAL, { replace: true });
      }
    }
  }, [status, user, profile, navigate, location.pathname]);

  const adminSettings = useAuth((s) => s.adminSettings);
  const isSuspended = adminSettings?.accountStatus === 'suspended';
  
  // Safely calculate expiry
  const expiresAtValue = adminSettings?.expiresAt;
  const expiryTime = typeof expiresAtValue === 'number' 
    ? expiresAtValue 
    : (expiresAtValue as any)?.toDate?.()?.getTime() || 0;

  const isExpired = expiryTime > 0 && expiryTime < Date.now();
  const isBlocked = !!(isSuspended || isExpired);
  const isPlanPage = location.pathname === ROUTES.SETTINGS.PAYMENTS_FROM_PLAN;

  if (status === 'loading' || status === 'idle' || (user && !profile)) {
    return (
      <div className="dark bg-background flex min-h-screen flex-col items-center justify-center">
        <div className="flex flex-col items-center gap-6">
          {/* Logo con efecto de respiración (pulse) */}
          <div className="relative">
            {/* El halo dorado de fondo también respira junto con el logo */}
            <div className="bg-ring/20 absolute inset-0 animate-pulse rounded-full blur-xl" />
            <img
              src="/logo.svg"
              alt="Cargando TuNomina..."
              className="relative z-10 h-24 animate-pulse drop-shadow-sm"
            />
          </div>
        </div>
      </div>
    );
  }

  if (isBlocked && !isPlanPage) {
    return hasPendingPayment ? <ProcessingScreen /> : <BlockedScreen />;
  }

  return (
    <>
      <ScrollToTop />
      <PostHogPageView />
      <Outlet />
    </>
  );
}
